import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Rocket, UploadCloud } from "lucide-react";
import { FileExplorer } from "@/components/file-explorer";
import { WIZARD_STEPS as steps, WizardProgress, StepCloud, StepNaming, StepEnvironments, StepResources, StepReview } from "@/components/generator-steps";

export default function Generator() {
  const nav = useNavigate();
  const [rules, setRules] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [step, setStep] = useState(0);
  const [preview, setPreview] = useState(null);
  const [creating, setCreating] = useState(false);
  const [hasConnectors, setHasConnectors] = useState(false);
  const [form, setForm] = useState({
    name: "cor-",
    cloud: "azure",
    language: "terraform",
    region: "",
    description: "",
    resources: [],
    tags: {},
    ci_platform: "github",
    environments: [],
    env_overrides: {},
  });

  useEffect(() => {
    Promise.all([
      client.get("/governance/rules"),
      client.get("/catalog/resources"),
    ]).then(([r, c]) => {
      setRules(r.data);
      setCatalog(c.data);
      const tags = {};
      (r.data.mandatory_tags || []).filter((t) => t !== "environment").forEach((t) => (tags[t] = ""));
      setForm((f) => ({ ...f, tags, region: r.data.allowed_regions?.[f.cloud]?.[0] || "", ci_platform: r.data.ci_platform || "github", environments: [r.data.allowed_environments?.[0]].filter(Boolean) }));
    });
    client.get("/connectors").then((r) => setHasConnectors(r.data.length > 0)).catch(() => {});
  }, []);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setTag = (k, v) => setForm((f) => ({ ...f, tags: { ...f.tags, [k]: v } }));

  const cloudCatalog = useMemo(() => catalog.filter((r) => r.cloud === form.cloud), [catalog, form.cloud]);

  const toggleResource = (key) => {
    setForm((f) => ({
      ...f,
      resources: f.resources.includes(key) ? f.resources.filter((k) => k !== key) : [...f.resources, key],
    }));
  };

  const doPreview = useCallback(async () => {
    try {
      const { data } = await client.post("/projects/preview", form);
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  }, [form]);

  const isReviewStep = step === steps.length - 1;
  useEffect(() => {
    if (isReviewStep) doPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- preview is fetched when entering the review step, not on every keystroke
  }, [isReviewStep]);

  const next = () => setStep((s) => Math.min(s + 1, steps.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const create = async (thenPush = false) => {
    setCreating(true);
    try {
      const { data } = await client.post("/projects", form);
      toast.success(`Project ${data.name} created`);
      nav(`/app/projects/${data.id}${thenPush ? "?push=1" : ""}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setCreating(false);
    }
  };

  if (!rules) return <div className="p-10 text-sm text-slate-500">Loading policies…</div>;

  return (
    <div className="grid min-h-screen grid-cols-[1fr_28rem]">
      {/* Left: form */}
      <div className="border-r border-slate-200 bg-white p-10">
        <div className="mb-6">
          <p className="font-mono-tight text-xs uppercase tracking-widest text-slate-500">// generator</p>
          <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight" data-testid="page-title">Scaffold a new project</h1>
          <p className="mt-1 text-sm text-slate-600">Walk through five steps. Toastern enforces your governance at every one.</p>
        </div>
        <WizardProgress step={step} />

        <div className="max-w-2xl">
          {step === 0 && <StepCloud form={form} rules={rules} setF={setF} />}
          {step === 1 && <StepNaming form={form} rules={rules} setF={setF} />}
          {step === 2 && <StepEnvironments form={form} rules={rules} setF={setF} setTag={setTag} />}
          {step === 3 && <StepResources form={form} cloudCatalog={cloudCatalog} toggleResource={toggleResource} setF={setF} />}
          {step === 4 && <StepReview form={form} rules={rules} cloudCatalog={cloudCatalog} preview={preview} />}
        </div>

        <div className="mt-10 flex items-center justify-between border-t border-slate-100 pt-6">
          <Button variant="ghost" onClick={back} disabled={step === 0} data-testid="wizard-back">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back
          </Button>
          {step < steps.length - 1 ? (
            <Button onClick={next} className="h-10 rounded-md bg-slate-900 text-white hover:bg-slate-800" data-testid="wizard-next">
              Next <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              {hasConnectors && (
                <Button variant="outline" onClick={() => create(true)} disabled={creating} className="h-10 rounded-md border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white" data-testid="wizard-create-push">
                  <UploadCloud className="mr-2 h-4 w-4" /> Generate &amp; push to Git
                </Button>
              )}
              <Button onClick={() => create(false)} disabled={creating} className="h-10 rounded-md bg-slate-900 text-white hover:bg-slate-800" data-testid="wizard-create">
                <Rocket className="mr-2 h-4 w-4" /> {creating ? "Generating…" : "Generate project"}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Right: live code preview */}
      <div className="sticky top-0 max-h-screen overflow-hidden bg-slate-950 text-slate-200">
        <div className="flex items-center gap-2 border-b border-slate-800 px-5 py-3">
          <span className="h-2 w-2 rounded-full bg-red-400/70" />
          <span className="h-2 w-2 rounded-full bg-amber-400/70" />
          <span className="h-2 w-2 rounded-full bg-emerald-400/70" />
          <span className="ml-3 font-mono-tight text-[11px] text-slate-500">
            {form.name} · {preview?.files?.length || 0} files
          </span>
        </div>
        <div className="max-h-[calc(100vh-3rem)] overflow-auto">
          {preview?.files?.length ? (
            <FileExplorer files={preview.files} root={form.name} height="calc(100vh - 6rem)" />
          ) : (
            <p className="p-5 font-mono-tight text-xs text-slate-500">Preview will render at the review step.</p>
          )}
        </div>
      </div>
    </div>
  );
}
